import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useMutation } from '@apollo/client';
import { TextField, Button } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { ChangeCurrentUserPasswordMutation } from '@modules/auth/graphql';

import type {
    ChangeCurrentUserPasswordMutationType,
    ChangeCurrentUserPasswordMutationVariables,
} from '@modules/types/graphql';

type ChangePasswordFormProps = {
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const ChangePasswordForm = (props: ChangePasswordFormProps): React.ReactElement => {
    const { onCancel } = props;

    const { enqueueSuccess } = useEnqueueStacks();

    const [changePassword, { loading: changePasswordLoading }] = useMutation<
        ChangeCurrentUserPasswordMutationType,
        ChangeCurrentUserPasswordMutationVariables
    >(ChangeCurrentUserPasswordMutation);

    return (
        <ExtendedFormik
            validationSchema={yup.object({
                oldPassword: yup.string().trim().required('Обязательное поле'),
                newPassword: yup
                    .string()
                    .trim()
                    .required('Обязательное поле')
                    .notOneOf(
                        [yup.ref('oldPassword'), null],
                        'Новый пароль не должен совпадать с текущим',
                    )
                    .min(8, 'Пароль должен содержать не менее 8 символов'),
                confirmNewPassword: yup
                    .string()
                    .trim()
                    .required('Обязательное поле')
                    .oneOf([yup.ref('newPassword'), null], 'Новые пароли должны совпадать'),
            })}
            initialValues={{
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: '',
            }}
            onSubmit={async values => {
                try {
                    await changePassword({
                        variables: values,
                    });

                    enqueueSuccess('Пароль был изменен!');
                    onCancel();
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, handleChange, handleBlur, isSubmitting } = formikProps;

                return (
                    <StyledForm>
                        <TextField
                            required
                            fullWidth
                            type='password'
                            id='oldPassword'
                            label='Старый пароль'
                            placeholder='Например, 12345678'
                            value={values.oldPassword}
                            error={!!errors.oldPassword}
                            helperText={errors.oldPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            required
                            fullWidth
                            type='password'
                            id='newPassword'
                            label='Новый пароль'
                            placeholder='Например, 12345678'
                            value={values.newPassword}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            required
                            fullWidth
                            type='password'
                            id='confirmNewPassword'
                            label='Новый пароль еще раз'
                            placeholder='Например, 12345678'
                            value={values.confirmNewPassword}
                            error={!!errors.confirmNewPassword}
                            helperText={errors.confirmNewPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <FormRowSubmit>
                            <Button
                                variant='outlined'
                                disabled={isSubmitting || changePasswordLoading}
                                onClick={onCancel}
                            >
                                Отмена
                            </Button>

                            <Button type='submit' loading={isSubmitting || changePasswordLoading}>
                                Сохранить
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { ChangePasswordForm };
